import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, RegionSelector } from '../common';
import { useAuth } from '../../context';
import api from '../../services/api';
import { reportsV2Service } from '../../services/reportsV2Service';
import { fitcloudService } from '../../services/fitcloudService';
import './ReportGenerationPage.css';

const ReportGenerationPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [config, setConfig] = useState({
    selectedAccount: null,
    authorName: 'AWS SaltWare Platform',
    roleArn: '',
    regions: ['ap-northeast-2'], // 다중 리전 배열
    reportStartDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    reportEndDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
    collectServiceInfo: false,
    selectedServices: new Set(),
    collectMetrics: false,
    metricConfigs: [],
    addInspectionItems: false,
    selectedInspectionItems: [],
    enableAIInspection: false
  });
  
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  const filteredAccounts = accounts.filter(account => 
    account.corpName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.accountName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.accountId.includes(searchTerm)
  );

  useEffect(() => {
    loadAccounts();
    
    // 드롭다운 외부 클릭 시 닫기
    const handleClickOutside = (event) => {
      if (!event.target.closest('.account-search-container')) {
        setShowDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadAccounts = async () => {
    try {
      setLoadingAccounts(true);
      const response = await fitcloudService.getAccounts();
      setAccounts(response.data.body || []);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const serviceOptions = [
    { key: 'ec2', label: '🖥️ EC2', name: 'EC2' },
    { key: 's3', label: '🪣 S3', name: 'S3' },
    { key: 'rds', label: '🗄️ RDS', name: 'RDS' },
    { key: 'iam', label: '👤 IAM', name: 'IAM' },
    { key: 'vpc', label: '🌐 VPC', name: 'VPC' },
    { key: 'elb', label: '⚖️ ELB', name: 'ELB' },
    { key: 'cloudfront', label: '🌐 CloudFront', name: 'CloudFront' },
    { key: 'ecs', label: '📦 ECS', name: 'ECS' },
    { key: 'gateway', label: '🌐 Gateway', name: 'Gateway' },
    { key: 'route53', label: '🌐 Route53', name: 'Route53' },
    { key: 'routetable', label: '🛣️ RouteTable', name: 'RouteTable' },
    { key: 'securitygroup', label: '🛡️ SecurityGroup', name: 'SecurityGroup' },
    { key: 'waf', label: '🔥 WAF', name: 'WAF' }
  ];

  const metricServices = [
    { 
      key: 'ec2', 
      label: '🖥️ EC2', 
      name: 'EC2',
      metrics: [
        { key: 'cpu', label: 'CPU 사용률' },
        { key: 'memory', label: '메모리 사용률' }
      ]
    },
    { 
      key: 'rds', 
      label: '🗄️ RDS', 
      name: 'RDS',
      metrics: [
        { key: 'cpu', label: 'CPU 사용률' },
        { key: 'memory', label: '메모리 사용률' }
      ]
    }
  ];

  const metricTypes = [
    { key: 'min', label: '최소' },
    { key: 'average', label: '평균' },
    { key: 'max', label: '최대' }
  ];

  const [inspectionItems, setInspectionItems] = useState({});
  const [loadingInspectionItems, setLoadingInspectionItems] = useState(false);

  useEffect(() => {
    const loadInspectionItems = async () => {
      setLoadingInspectionItems(true);
      try {
        const response = await api.get('/inspection-items');
        setInspectionItems(response.data);
      } catch (error) {
        console.error('Failed to load inspection items:', error);
      } finally {
        setLoadingInspectionItems(false);
      }
    };
    loadInspectionItems();
  }, []);

  const handleConfigChange = (key, value) => {
    setConfig(prev => {
      const newConfig = {
        ...prev,
        [key]: value
      };
      
      // 메트릭 수집을 활성화할 때 기본 설정 자동 추가
      if (key === 'collectMetrics' && value === true && prev.metricConfigs.length === 0) {
        const baseTime = Date.now();
        newConfig.metricConfigs = [
          {
            id: baseTime + 1,
            name: 'EC2 CPU 최대값',
            startDate: prev.reportStartDate,
            endDate: prev.reportEndDate,
            filterCount: 5,
            period: 86400,
            service: 'ec2',
            metric: 'cpu',
            type: 'max'
          },
          {
            id: baseTime + 2,
            name: 'EC2 메모리 최대값',
            startDate: prev.reportStartDate,
            endDate: prev.reportEndDate,
            filterCount: 5,
            period: 86400,
            service: 'ec2',
            metric: 'memory',
            type: 'max'
          },
          {
            id: baseTime + 3,
            name: 'RDS CPU 최대값',
            startDate: prev.reportStartDate,
            endDate: prev.reportEndDate,
            filterCount: 5,
            period: 86400,
            service: 'rds',
            metric: 'cpu',
            type: 'max'
          },
          {
            id: baseTime + 4,
            name: 'RDS 메모리 최대값',
            startDate: prev.reportStartDate,
            endDate: prev.reportEndDate,
            filterCount: 5,
            period: 86400,
            service: 'rds',
            metric: 'memory',
            type: 'max'
          }
        ];
      }
      
      return newConfig;
    });
  };

  const toggleService = (service) => {
    setConfig(prev => {
      const newServices = new Set(prev.selectedServices);
      if (newServices.has(service)) {
        newServices.delete(service);
      } else {
        newServices.add(service);
      }
      return { ...prev, selectedServices: newServices };
    });
  };

  const [editingConfig, setEditingConfig] = useState(null);

  const ALL_PERIODS = [
    { value: 300,   label: '5분' },
    { value: 900,   label: '15분' },
    { value: 3600,  label: '1시간' },
    { value: 21600, label: '6시간' },
    { value: 86400, label: '1일' }
  ];
  const MAX_DATAPOINTS = 100;

  const getAvailablePeriods = (startDate, endDate) => {
    if (!startDate || !endDate) return ALL_PERIODS;
    const days = (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24);
    const totalSeconds = days * 24 * 60 * 60;
    const minPeriod = Math.ceil(totalSeconds / MAX_DATAPOINTS);
    return ALL_PERIODS.filter(p => p.value >= minPeriod);
  };

  const getRecommendedPeriod = (startDate, endDate) => {
    const available = getAvailablePeriods(startDate, endDate);
    return available[0]?.value ?? 86400;
  };

  const addMetricConfig = () => {
    const configCount = config.metricConfigs.length + 1;
    
    const newConfig = {
      id: Date.now(),
      name: `메트릭 설정 ${configCount}`,
      startDate: config.reportStartDate,
      endDate: config.reportEndDate,
      filterCount: 5,
      period: 3600,
      service: 'ec2',
      metric: 'cpu',
      type: 'average'
    };
    
    setConfig(prev => ({
      ...prev,
      metricConfigs: [...prev.metricConfigs, newConfig]
    }));
    setEditingConfig(newConfig.id);
  };

  const saveMetricConfig = (configId) => {
    setEditingConfig(null);
  };

  const cancelEdit = (configId) => {
    setEditingConfig(null);
  };

  const removeMetricConfig = (configId) => {
    setConfig(prev => ({
      ...prev,
      metricConfigs: prev.metricConfigs.filter(config => config.id !== configId)
    }));
  };

  const updateMetricConfig = (configId, field, value) => {
    setConfig(prev => ({
      ...prev,
      metricConfigs: prev.metricConfigs.map(config => 
        config.id === configId ? { ...config, [field]: value } : config
      )
    }));
  };



  const toggleAllServices = () => {
    const allServices = serviceOptions.map(s => s.key);
    setConfig(prev => ({
      ...prev,
      selectedServices: prev.selectedServices.size === allServices.length ? new Set() : new Set(allServices)
    }));
  };

  const toggleAllInspectionItems = () => {
    const allItems = Object.values(inspectionItems).flatMap(service => 
      service.categories.flatMap(category => 
        category.items.map(item => item.id)
      )
    );
    setConfig(prev => ({
      ...prev,
      selectedInspectionItems: prev.selectedInspectionItems.length === allItems.length ? [] : allItems
    }));
  };

  const toggleServiceInspectionItems = (serviceType) => {
    const serviceItems = inspectionItems[serviceType]?.categories.flatMap(category => 
      category.items.map(item => item.id)
    ) || [];
    
    const allServiceItemsSelected = serviceItems.every(itemId => 
      config.selectedInspectionItems.includes(itemId)
    );
    
    setConfig(prev => ({
      ...prev,
      selectedInspectionItems: allServiceItemsSelected
        ? prev.selectedInspectionItems.filter(id => !serviceItems.includes(id))
        : [...new Set([...prev.selectedInspectionItems, ...serviceItems])]
    }));
  };

  const [isGenerating, setIsGenerating] = useState(false);

  const validateSelection = () => {
    // 서비스 정보 수집 검증
    if (config.collectServiceInfo && config.selectedServices.size === 0) {
      return '서비스 정보 수집을 선택했다면 최소 1개 이상의 서비스를 선택해야 합니다.';
    }
    
    // 메트릭 수집 검증
    if (config.collectMetrics) {
      if (config.metricConfigs.length === 0) {
        return '메트릭 수집을 선택했다면 최소 1개 이상의 메트릭 설정을 추가해야 합니다.';
      }
      
      for (const metricConfig of config.metricConfigs) {
        if (!metricConfig.name.trim()) {
          return '모든 메트릭 설정에 이름을 입력해야 합니다.';
        }
        if (!metricConfig.service) {
          return `"${metricConfig.name}" 설정에서 서비스를 선택해야 합니다.`;
        }
        if (!metricConfig.metric) {
          return `"${metricConfig.name}" 설정에서 메트릭을 선택해야 합니다.`;
        }
        if (!metricConfig.type) {
          return `"${metricConfig.name}" 설정에서 메트릭 타입을 선택해야 합니다.`;
        }
      }
    }
    
    // 검사 항목 검증
    if (config.addInspectionItems && config.selectedInspectionItems.length === 0) {
      return '검사 항목 추가를 선택했다면 최소 1개 이상의 검사 항목을 선택해야 합니다.';
    }
    
    return null;
  };

  const handleGenerate = async () => {
    const validationError = validateSelection();
    if (validationError) {
      alert(validationError);
      return;
    }
    
    setIsGenerating(true);
    try {
      const reportConfig = {
        regions: config.regions, // 다중 리전 배열
        collectServiceInfo: config.collectServiceInfo,
        ...(config.collectServiceInfo && {
          selectedServices: Array.from(config.selectedServices)
        }),
        collectMetrics: config.collectMetrics,
        ...(config.collectMetrics && {
          metricConfigs: config.metricConfigs.map(metricConfig => ({
            name: metricConfig.name,
            startDate: metricConfig.startDate,
            endDate: metricConfig.endDate,
            filterCount: metricConfig.filterCount,
            period: metricConfig.period,
            service: metricConfig.service,
            metric: metricConfig.metric,
            type: metricConfig.type
          }))
        }),
        addInspectionItems: config.addInspectionItems,
        ...(config.addInspectionItems && {
          selectedInspectionItems: config.selectedInspectionItems,
          enableAIInspection: config.enableAIInspection
        })
      };
      
      const reportData = {
        authorName: config.authorName,
        roleArn: config.roleArn,
        reportStartDate: config.reportStartDate,
        reportEndDate: config.reportEndDate,
        config: reportConfig
      };
      
      const result = await reportsV2Service.generateReport(reportData);
      
      // 성공 메시지 표시 및 보고서 목록으로 이동
      alert('보고서 생성이 시작되었습니다.');
      navigate('/report');
      
    } catch (error) {
      console.error('Report generation error:', error);
      alert('보고서 생성에 실패했습니다: ' + error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const getServiceIcon = (serviceType) => {
    const icons = {
      'VPC': '🌐',
      'EC2': '🖥️', 
      'EBS': '💾',
      'S3': '🪣',
      'RDS': '🗄️',
      'IAM': '👤',
      'ELB': '⚖️',
      'CloudFront': '🌐',
      'ECS': '📦',
      'Gateway': '🌐',
      'Route53': '🌐',
      'RouteTable': '🛣️',
      'SecurityGroup': '🛡️',
      'WAF': '🔥'
    };
    return icons[serviceType] || '🔧';
  };

  const handleCancel = () => {
    navigate('/report');
  };

  return (
    <div className="report-generation-page">
      <div className="report-generation-page__header">
        <h1>보고서 생성 설정</h1>
        <p>포함할 데이터와 검사 항목을 선택하여 맞춤형 보고서를 생성하세요.</p>
      </div>

      <div className="report-generation-page__content">
        
        {/* 보고서 기본 정보 */}
        <Card>
          <div className="config-section">
            <h3 className="section-title">보고서 기본 정보</h3>
            
            <div className="basic-info-grid">
              <div className="input-group">
                <label className="input-label">🌍 AWS 리전 * (다중 선택 가능)</label>
                <div className="multi-region-container">
                  <div className="selected-regions">
                    {config.regions.map(region => {
                      const regionData = [
                        { code: 'us-east-1', name: '미국 (버지니아 북부)' },
                        { code: 'us-east-2', name: '미국 (오하이오)' },
                        { code: 'us-west-1', name: '미국 (캘리포니아)' },
                        { code: 'us-west-2', name: '미국 (오레곤)' },
                        { code: 'ap-northeast-1', name: '아시아 태평양 (도쿄)' },
                        { code: 'ap-northeast-2', name: '아시아 태평양 (서울)' },
                        { code: 'ap-southeast-1', name: '아시아 태평양 (싱가포르)' },
                        { code: 'ap-southeast-2', name: '아시아 태평양 (시드니)' },
                        { code: 'eu-central-1', name: '유럽 (프랑크푸르트)' },
                        { code: 'eu-west-1', name: '유럽 (아일랜드)' },
                        { code: 'eu-west-2', name: '유럽 (런던)' },
                        { code: 'ca-central-1', name: '캐나다 (중부)' },
                        { code: 'sa-east-1', name: '남아메리카 (상파울루)' }
                      ].find(r => r.code === region);
                      return (
                        <span key={region} className="region-tag">
                          {regionData?.code}
                          <button
                            type="button"
                            onClick={() => handleConfigChange('regions', config.regions.filter(r => r !== region))}
                            className="region-tag-remove"
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value && !config.regions.includes(e.target.value)) {
                        handleConfigChange('regions', [...config.regions, e.target.value]);
                      }
                    }}
                    className="text-input"
                  >
                    <option value="">+ 리전 추가</option>
                    <option value="us-east-1">us-east-1 - 미국 (버지니아 북부)</option>
                    <option value="us-east-2">us-east-2 - 미국 (오하이오)</option>
                    <option value="us-west-1">us-west-1 - 미국 (캘리포니아)</option>
                    <option value="us-west-2">us-west-2 - 미국 (오레곤)</option>
                    <option value="ap-northeast-1">ap-northeast-1 - 아시아 태평양 (도쿄)</option>
                    <option value="ap-northeast-2">ap-northeast-2 - 아시아 태평양 (서울)</option>
                    <option value="ap-southeast-1">ap-southeast-1 - 아시아 태평양 (싱가포르)</option>
                    <option value="ap-southeast-2">ap-southeast-2 - 아시아 태평양 (시드니)</option>
                    <option value="eu-central-1">eu-central-1 - 유럽 (프랑크푸르트)</option>
                    <option value="eu-west-1">eu-west-1 - 유럽 (아일랜드)</option>
                    <option value="eu-west-2">eu-west-2 - 유럽 (런던)</option>
                    <option value="ca-central-1">ca-central-1 - 캐나다 (중부)</option>
                    <option value="sa-east-1">sa-east-1 - 남아메리카 (상파울루)</option>
                  </select>
                </div>
              </div>
              
              <div className="input-group">
                <label className="input-label">🏢 AWS 계정 선택 *</label>
                {loadingAccounts ? (
                  <div>계정 목록을 불러오는 중...</div>
                ) : (
                  <div className="account-search-container">
                    <input
                      type="text"
                      value={config.selectedAccount ? `${config.selectedAccount.corpName} - ${config.selectedAccount.accountName} (${config.selectedAccount.accountId})` : searchTerm}
                      onChange={(e) => {
                        if (!config.selectedAccount) {
                          setSearchTerm(e.target.value);
                          setShowDropdown(true);
                        }
                      }}
                      onFocus={() => setShowDropdown(true)}
                      placeholder="계정을 검색하세요"
                      className="text-input"
                      required
                    />
                    {config.selectedAccount && (
                      <button
                        type="button"
                        onClick={() => {
                          setConfig(prev => ({ ...prev, selectedAccount: null, roleArn: '' }));
                          setSearchTerm('');
                          setShowDropdown(true);
                        }}
                        className="clear-selection-btn"
                      >
                        ×
                      </button>
                    )}
                    {showDropdown && !config.selectedAccount && (
                      <div className="account-dropdown">
                        {filteredAccounts.length > 0 ? (
                          filteredAccounts.map(account => (
                            <div
                              key={account.accountId}
                              onClick={() => {
                                const defaultRoleArn = `arn:aws:iam::${account.accountId}:role/SaltwareCrossAccount`;
                                setConfig(prev => ({
                                  ...prev,
                                  selectedAccount: account,
                                  roleArn: prev.roleArn || defaultRoleArn // 기존 값이 없을 때만 기본값 설정
                                }));
                                setShowDropdown(false);
                                setSearchTerm('');
                              }}
                              className="account-option"
                            >
                              <div className="account-info">
                                <div className="account-name">{account.corpName} - {account.accountName}</div>
                                <div className="account-id">{account.accountId}</div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="no-results">검색 결과가 없습니다</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="input-group">
                <label className="input-label">👤 보고서 생성자 *</label>
                <input
                  type="text"
                  value={config.authorName}
                  onChange={(e) => handleConfigChange('authorName', e.target.value)}
                  placeholder="생성자 이름을 입력하세요"
                  className="text-input"
                  required
                />
              </div>
              
              <div className="input-group">
                <label className="input-label">🔑 Role ARN *</label>
                <input
                  type="text"
                  value={config.roleArn}
                  onChange={(e) => handleConfigChange('roleArn', e.target.value)}
                  className="text-input"
                  placeholder="Role ARN을 입력하세요 (예: arn:aws:iam::123456789012:role/YourRoleName)"
                  required
                />
                {config.selectedAccount && (
                  <div className="input-hint">
                    💡 기본값: arn:aws:iam::{config.selectedAccount.accountId}:role/SaltwareCrossAccount
                  </div>
                )}
              </div>
              
              <div className="date-range-group">
                <label className="input-label">📅 보고서 기간 *</label>
                <div className="date-inputs">
                  <input
                    type="date"
                    value={config.reportStartDate}
                    onChange={(e) => handleConfigChange('reportStartDate', e.target.value)}
                    className="date-input"
                    required
                  />
                  <span className="date-separator">~</span>
                  <input
                    type="date"
                    value={config.reportEndDate}
                    onChange={(e) => handleConfigChange('reportEndDate', e.target.value)}
                    className="date-input"
                    required
                  />
                </div>
              </div>
            </div>
          </div>
        </Card>
        
        {/* 서비스 정보 수집 */}
        <Card>
          <div className="config-section">
            <label className="main-checkbox-option">
              <input
                type="checkbox"
                checked={config.collectServiceInfo}
                onChange={(e) => handleConfigChange('collectServiceInfo', e.target.checked)}
              />
              <div className="option-content">
                <div className="option-title">서비스 정보 수집</div>
                <div className="option-description">AWS 서비스 리소스 정보를 수집합니다</div>
              </div>
            </label>
            
            {config.collectServiceInfo && (
              <div className="sub-options">
                <div className="sub-options-header">
                  <h4>수집할 서비스 선택</h4>
                  <button
                    type="button"
                    onClick={toggleAllServices}
                    className="toggle-all-btn"
                  >
                    {config.selectedServices.size === serviceOptions.length ? '전체 해제' : '전체 선택'}
                  </button>
                </div>
                <div className="options-grid">
                  {serviceOptions.map(service => (
                    <label key={service.key} className="checkbox-option">
                      <input
                        type="checkbox"
                        checked={config.selectedServices.has(service.key)}
                        onChange={() => toggleService(service.key)}
                      />
                      <span>{service.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* 메트릭 수집 */}
        <Card>
          <div className="config-section">
            <label className="main-checkbox-option">
              <input
                type="checkbox"
                checked={config.collectMetrics}
                onChange={(e) => handleConfigChange('collectMetrics', e.target.checked)}
              />
              <div className="option-content">
                <div className="option-title">메트릭 수집</div>
                <div className="option-description">CloudWatch 메트릭 데이터를 수집합니다</div>
              </div>
            </label>
            
            {config.collectMetrics && (
              <div className="sub-options">
                <div className="metric-configs-header">
                  <h4>메트릭 수집 설정</h4>
                  <Button variant="secondary" size="sm" onClick={addMetricConfig}>
                    + 설정 추가
                  </Button>
                </div>
                
                {config.metricConfigs.map((metricConfig, index) => {
                  const isEditing = editingConfig === metricConfig.id;
                  const isComplete = metricConfig.name && metricConfig.service && metricConfig.metric && metricConfig.type;
                  
                  return (
                    <Card key={metricConfig.id} className="card card--padding-md metric-config-item">
                      <div className="metric-config-header">
                        <div className="config-title">
                          {isEditing ? (
                            <input
                              type="text"
                              value={metricConfig.name}
                              onChange={(e) => updateMetricConfig(metricConfig.id, 'name', e.target.value)}
                              className="config-name-input"
                              placeholder="설정 이름 입력"
                              autoFocus
                            />
                          ) : (
                            <h4 className="config-name">{metricConfig.name}</h4>
                          )}
                        </div>
                        
                        <div className="config-actions">
                          {isEditing ? (
                            <>
                              <Button 
                                variant="primary" 
                                size="sm" 
                                onClick={() => saveMetricConfig(metricConfig.id)}
                                disabled={!isComplete}
                              >
                                저장
                              </Button>
                              <Button 
                                variant="secondary" 
                                size="sm" 
                                onClick={() => cancelEdit(metricConfig.id)}
                              >
                                취소
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button 
                                variant="secondary" 
                                size="sm" 
                                onClick={() => setEditingConfig(metricConfig.id)}
                              >
                                수정
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => removeMetricConfig(metricConfig.id)}
                                className="remove-btn"
                              >
                                삭제
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {!isEditing && isComplete && (
                        <div className="config-summary">
                          <div className="summary-item">
                            <span className="summary-label">서비스:</span>
                            <span className="summary-value">{metricServices.find(s => s.key === metricConfig.service)?.name}</span>
                          </div>
                          <div className="summary-item">
                            <span className="summary-label">메트릭:</span>
                            <span className="summary-value">{metricServices.find(s => s.key === metricConfig.service)?.metrics.find(m => m.key === metricConfig.metric)?.label}</span>
                          </div>
                          <div className="summary-item">
                            <span className="summary-label">타입:</span>
                            <span className="summary-value">{metricTypes.find(t => t.key === metricConfig.type)?.label}</span>
                          </div>
                          <div className="summary-item">
                            <span className="summary-label">기간:</span>
                            <span className="summary-value">{metricConfig.startDate} ~ {metricConfig.endDate}</span>
                          </div>
                          <div className="summary-item">
                            <span className="summary-label">상위 인스턴스:</span>
                            <span className="summary-value">{metricConfig.filterCount}개</span>
                          </div>
                        </div>
                      )}
                      
                      {(isEditing || !isComplete) && (
                        <div className="metric-config-form">
                          <div className="form-row">
                            <div className="form-group">
                              <label>시작 날짜</label>
                              <input
                                type="date"
                                value={metricConfig.startDate}
                                onChange={(e) => {
                                  const newStart = e.target.value;
                                  updateMetricConfig(metricConfig.id, 'startDate', newStart);
                                  const available = getAvailablePeriods(newStart, metricConfig.endDate);
                                  if (!available.find(p => p.value === metricConfig.period)) {
                                    updateMetricConfig(metricConfig.id, 'period', available[0]?.value ?? 86400);
                                  }
                                }}
                                className="form-input"
                                disabled={!isEditing}
                              />
                            </div>
                            <div className="form-group">
                              <label>끝 날짜</label>
                              <input
                                type="date"
                                value={metricConfig.endDate}
                                onChange={(e) => {
                                  const newEnd = e.target.value;
                                  updateMetricConfig(metricConfig.id, 'endDate', newEnd);
                                  const available = getAvailablePeriods(metricConfig.startDate, newEnd);
                                  if (!available.find(p => p.value === metricConfig.period)) {
                                    updateMetricConfig(metricConfig.id, 'period', available[0]?.value ?? 86400);
                                  }
                                }}
                                className="form-input"
                                disabled={!isEditing}
                              />
                            </div>
                          </div>
                          
                          <div className="form-row">
                            <div className="form-group">
                              <label>상위 인스턴스 수</label>
                              <input
                                type="number"
                                value={metricConfig.filterCount}
                                onChange={(e) => updateMetricConfig(metricConfig.id, 'filterCount', parseInt(e.target.value) || 5)}
                                min="1"
                                max="20"
                                className="form-input"
                                disabled={!isEditing}
                              />
                            </div>
                            <div className="form-group">
                              <label>데이터 간격</label>
                              <select
                                value={metricConfig.period}
                                onChange={(e) => updateMetricConfig(metricConfig.id, 'period', parseInt(e.target.value))}
                                className="form-input"
                                disabled={!isEditing}
                              >
                                {getAvailablePeriods(metricConfig.startDate, metricConfig.endDate).map(p => (
                                  <option key={p.value} value={p.value}>{p.label}</option>
                                ))}
                              </select>
                              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                                ※ 최대 {MAX_DATAPOINTS}개 데이터포인트 기준
                              </div>
                            </div>
                          </div>
                          
                          <div className="form-row">
                            <div className="form-group">
                              <label>서비스</label>
                              <select
                                value={metricConfig.service}
                                onChange={(e) => {
                                  updateMetricConfig(metricConfig.id, 'service', e.target.value);
                                  updateMetricConfig(metricConfig.id, 'metric', '');
                                  updateMetricConfig(metricConfig.id, 'type', '');
                                }}
                                className="form-input"
                                disabled={!isEditing}
                              >
                                <option value="">서비스 선택</option>
                                {metricServices.map(service => (
                                  <option key={service.key} value={service.key}>{service.name}</option>
                                ))}
                              </select>
                            </div>
                            
                            <div className="form-group">
                              <label>메트릭</label>
                              <select
                                value={metricConfig.metric}
                                onChange={(e) => {
                                  updateMetricConfig(metricConfig.id, 'metric', e.target.value);
                                  updateMetricConfig(metricConfig.id, 'type', '');
                                }}
                                className="form-input"
                                disabled={!isEditing || !metricConfig.service}
                              >
                                <option value="">메트릭 선택</option>
                                {metricConfig.service && metricServices.find(s => s.key === metricConfig.service)?.metrics.map(metric => (
                                  <option key={metric.key} value={metric.key}>{metric.label}</option>
                                ))}
                              </select>
                            </div>
                            
                            <div className="form-group">
                              <label>메트릭 타입</label>
                              <select
                                value={metricConfig.type}
                                onChange={(e) => updateMetricConfig(metricConfig.id, 'type', e.target.value)}
                                className="form-input"
                                disabled={!isEditing || !metricConfig.metric}
                              >
                                <option value="">타입 선택</option>
                                {metricTypes.map(type => (
                                  <option key={type.key} value={type.key}>{type.label}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {!isComplete && (
                        <div className="config-status">
                          <span className="status-warning">⚠️ 설정을 완료해주세요</span>
                        </div>
                      )}
                    </Card>
                  );
                })}
                
                {config.metricConfigs.length === 0 && (
                  <div className="empty-state">
                    <p>메트릭 수집 설정을 추가해주세요.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* 검사 항목 추가 */}
        <Card>
          <div className="config-section">
            <label className="main-checkbox-option">
              <input
                type="checkbox"
                checked={config.addInspectionItems}
                onChange={(e) => handleConfigChange('addInspectionItems', e.target.checked)}
              />
              <div className="option-content">
                <div className="option-title">검사 항목 추가</div>
                <div className="option-description">리소스 검사 결과를 포함합니다</div>
              </div>
            </label>
            
            {config.addInspectionItems && (
              <div className="sub-options">
                <div className="sub-options-header">
                  <h4>포함할 검사 항목 선택</h4>
                  <button
                    type="button"
                    onClick={toggleAllInspectionItems}
                    className="toggle-all-btn"
                  >
                    {config.selectedInspectionItems.length === Object.values(inspectionItems).flatMap(service => service.categories.flatMap(category => category.items.map(item => item.id))).length ? '전체 해제' : '전체 선택'}
                  </button>
                </div>
                {loadingInspectionItems ? (
                  <div className="loading-text">검사 항목을 불러오는 중...</div>
                ) : (
                  <div className="inspection-items-list">
                    {Object.entries(inspectionItems).map(([serviceType, service]) => (
                      <div key={serviceType} className="service-group">
                        <div className="service-group-header">
                          <div className="service-info">
                            <div className="service-icon">{getServiceIcon(serviceType)}</div>
                            <h5 className="service-group-title">{service.name || serviceType}</h5>
                          </div>
                          <button
                            type="button"
                            onClick={() => toggleServiceInspectionItems(serviceType)}
                            className="toggle-service-btn"
                          >
                            {service.categories.flatMap(category => category.items.map(item => item.id)).every(itemId => config.selectedInspectionItems.includes(itemId)) ? '해제' : '선택'}
                          </button>
                        </div>
                        {service.categories.map((category, categoryIndex) => (
                          <div key={`${serviceType}-${categoryIndex}`} className="category-group">
                            <h6 className="category-title">{category.name}</h6>
                            <div className="items-grid">
                              {category.items.map((item, itemIndex) => {
                                const itemKey = item.id;
                                return (
                                  <label key={`${serviceType}-${categoryIndex}-${itemIndex}`} className="inspection-item-option">
                                    <input
                                      type="checkbox"
                                      checked={config.selectedInspectionItems.includes(itemKey)}
                                      onChange={() => setConfig(prev => ({
                                        ...prev,
                                        selectedInspectionItems: prev.selectedInspectionItems.includes(itemKey)
                                          ? prev.selectedInspectionItems.filter(id => id !== itemKey)
                                          : [...prev.selectedInspectionItems, itemKey]
                                      }))}
                                    />
                                    <div className="item-content">
                                      <div className="item-title">{item.name}</div>
                                      <div className="item-description">{item.description}</div>
                                    </div>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
                
                {config.selectedInspectionItems.length > 0 && (
                  <div className="ai-inspection-option">
                    <label className="checkbox-option">
                      <input
                        type="checkbox"
                        checked={config.enableAIInspection}
                        onChange={(e) => handleConfigChange('enableAIInspection', e.target.checked)}
                      />
                      <div className="option-content">
                        <div className="option-title">🤖 AI 검사 포함</div>
                        <div className="option-description">선택된 검사 항목에 대한 AI 기반 분석 결과를 포함합니다</div>
                      </div>
                    </label>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>



        <div className="page-actions">
          <Button variant="secondary" onClick={handleCancel}>
            취소
          </Button>
          <Button 
            variant="primary" 
            onClick={handleGenerate}
            disabled={!config.selectedAccount || !config.authorName.trim() || !config.roleArn.trim() || !config.reportStartDate || !config.reportEndDate || isGenerating}
            loading={isGenerating}
          >
            {isGenerating ? '생성 중...' : '보고서 생성'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReportGenerationPage;